import React from 'react';
import { observer } from 'mobx-react-lite';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { productAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import useMarketLandscapeTab from '../../../../../MarketLandscapeTab/useMarketLandscapeTab.logic';
import './MarketStructureChart.styles.scss';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, Title, Tooltip, Legend);

const MarketStructureChart: React.FC = observer(() => {
  const {
    activeView,
    setActiveView,
    isLoading,
    hasError,
    hasNoData,
    errorMessage,
    marketShareChartData,
    marketShareChartOptions,
    currentPackSizeData,
  } = useMarketLandscapeTab(productAnalysisStore);

  // Skeleton loader for the chart
  const renderSkeleton = () => (
    <div className="market-structure-skeleton">
      <div className="skeleton-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-toggle"></div>
      </div>
      <div className="skeleton-chart"></div>
      <div className="skeleton-legend">
        <div className="skeleton-legend-item"></div>
        <div className="skeleton-legend-item"></div>
        <div className="skeleton-legend-item"></div>
      </div>
    </div>
  );

  // Render content based on state
  const renderContent = () => {
    if (isLoading) {
      return renderSkeleton();
    }

    if (hasError) {
      return (
        <div className="market-structure-error">
          <span className="error-icon">⚠️</span>
          <p>Error loading market structure data: {errorMessage}</p>
        </div>
      );
    }

    if (hasNoData || !currentPackSizeData) {
      return (
        <div className="market-structure-no-data">
          <span className="no-data-icon">📊</span>
          <p>No {activeView} market structure data available.</p>
        </div>
      );
    }

    return (
      <>
        <div className="market-structure-chart-container">
          {marketShareChartData ? (
            <div className="chart-wrapper">
              <Bar data={marketShareChartData} options={marketShareChartOptions} />
            </div>
          ) : (
            <div className="no-chart-data">No chart data available</div>
          )}
        </div>
        {/* <div className="market-structure-metrics">
          <div className="metric-item">
            <span className="metric-value">{currentPackSizeData.metrics.activeLabelers}</span>
            <span className="metric-label">Active Labelers</span>
          </div>
          <div className="metric-item">
            <span className="metric-value">{currentPackSizeData.metrics.newEntrants}</span>
            <span className="metric-label">New Entrants (12M)</span>
          </div>
        </div> */}
      </>
    );
  };

  return (
    <div className="market-structure-chart">
      <div className="market-structure-header">
        <h3 className="market-structure-title">Market Share by Manufacturer</h3>
        <div className="market-toggle">
          <button
            className={`toggle-btn ${activeView === 'government' ? 'active' : ''}`}
            onClick={() => setActiveView('government')}
          >
            Government
          </button>
          <button
            className={`toggle-btn ${activeView === 'commercial' ? 'active' : ''}`}
            onClick={() => setActiveView('commercial')}
          >
            Commercial
          </button>
        </div>
      </div>
      {renderContent()}
    </div>
  );
});

MarketStructureChart.displayName = 'MarketStructureChart';

export default MarketStructureChart;
